import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Button, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BusinessTax = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Business Tax Resource Hub | Simplify Your Tax Filing"
          description="Explore business tax form instructions, filing deadlines, schedules, deductions, credits and tax guides for S corporations and partnerships"
          keywords=""
          canonical="resources/business-tax/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ justifyContent: 'center', mt: 8, mb: 8 }}
        component="section"
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 1,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Business Tax </span>
          </span>{' '}
          Resource Center
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            fontStyle: 'italic',
            mb: 3,
          }}
        >
          S Corporation & Partnership
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 3,
          }}
        >
          Everything You Need to Understand, Prepare, and File Business Tax
          Returns, with clear resources <br />
          on requirements, forms, deadlines, deductions, and compliance.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href="#explore"
          >
            Explore Business Tax Resources
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        mt={0}
        component="section"
        id="explore"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                S Corporation
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Start with the basics of business tax filing, including Form
                1120-S requirements, key schedules, filing deadlines, and
                essential tax information.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand Form 1120-S filing requirements, key
                      instructions, required schedules, and important
                      information for filing an S corporation tax return.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read 1120-S Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-filing-deadlines-and-penalties-guide/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Required Dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S Due Date
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand Form 1120-S filing deadlines, extension dates,
                      and help your S corporation file on time.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">View Due dates</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-k-1/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule K-1 (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule K-1 reports each shareholder's share of
                      income, deductions, credits, and other tax items from an S
                      corporation.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Schedule K-1
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-m3-form-1120s/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Detailed Reporting
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule M-3 (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how Schedule M-3 reconciles financial statement
                      income with income reported on the S corporation tax
                      return.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Schedule M-3
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-b/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most Helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule B (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn about the additional information S corporations must
                      provide regarding ownership, tax elections, and other
                      filing requirements.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule B</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120s-schedule-d/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most Helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule D (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how to report capital gains and losses from the
                      sale or exchange of capital assets on Schedule D.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule D</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-k/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Income & Deductions
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule K (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule K reports the S corporation's total
                      income, deductions, credits, and other tax items passed
                      through to shareholders.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule K</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-l/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Balance Sheet
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule L (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how to report the S corporation's balance sheet
                      information, including assets, liabilities, and
                      shareholders' equity.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule L</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-m-1/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Reconciliation
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule M-1 (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule M-1 reconciles income or loss reported
                      on the corporation's books with income or loss reported on
                      the tax return.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule M-1</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-m-2/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Shareholders' Equity
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule M-2 (Form 1120-S)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how to analyze and report the Accumulated
                      Adjustments Account, Previously Taxed Income, Accumulated
                      Earnings and Profits, and Other Adjustments Account.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule M-2</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-mailing-address/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Filing Information
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S Mailing Address
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find the appropriate IRS mailing address for Form 1120-S
                      based on your filing situation and payment requirements.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      View Mailing Address
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box component="section" sx={{ my: 8 }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Partnership
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Start with the basics of partnership tax filing, including
                filing requirements, key schedules, important deadlines, mailing
                information, and other essential tax considerations for
                partnerships.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1065-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1065 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Overview of Form 1065 filing requirements and step-by-step
                      instructions for partnership tax returns.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read 1065 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1065-filing-deadlines-and-penalties-guide/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Required Dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1065 Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand Form 1065 filing deadlines and extension dates
                      based on the partnership's tax year.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">View Due Dates</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-k1-1065/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most Helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule K-1 (Form 1065)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule K-1 reports each partner's share of
                      income, deductions, credits, and other tax items.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Schedule K-1
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-m-3-form-1065/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most Helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule M-3 (Form 1065)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how Schedule M-3 reconciles financial statement
                      of income with income reported on the partnership tax
                      return.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule M-3</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-b-1-form-1065-instruction/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Partnership Information
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule B-1 (Form 1065)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule B-1 reports information about partners
                      who own significant interests in the partnership and
                      certain ownership changes.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule B-1</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-b-2-form-1065-instruction/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Partnership Elections
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule B-2 (Form 1065)
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Schedule B-2 is used by eligible partnerships to
                      elect out of the centralized partnership audit regime and
                      report the required partner information.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Schedule B-2</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1065-mailing-address/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Filing Information
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1065 Mailing Address
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find the correct IRS mailing address for Form 1065 based
                      on the partnership's filing requirements and payment
                      status.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      View Mailing Address
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box component="section" sx={{ backgroundColor: '#f0f2f9' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                  textAlign: 'center',
                }}
              >
                State Filing
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, textAlign: 'center' }}>
                Learn the basics of state business tax filing, including state
                requirements, filing deadlines, <br />
                tax considerations, and essential compliance information.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-100s-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'center',
                    display: 'grid',
                    alignContent: 'space-between',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      FTB CA Form 100S Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn about California Form 100S filing requirements,
                      instructions, deadlines, and key information S
                      corporations need for state tax filing.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read Form 100S Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box component="section" sx={{ backgroundColor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Related Forms
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Explore commonly used in partnership and S corporation filings,
                including their filing requirements, reporting guidelines,
                eligible deductions, and key information needed for accurate
                business tax filing.
              </Typography>
            </Grid>
            {/* IRS Form 1125-A */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1125-a/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Cost of Goods Sold
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 1125-A
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Form 1125-A is used to report the cost of goods
                      sold and calculate the gross profit or loss for a
                      business.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 1125-A
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* IRS Form 1125-E */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1125-e/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Officer Compensation
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 1125-E
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how Form 1125-E is used to report compensation
                      of officers for eligible corporations.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 1125-E
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* IRS Form 8825 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-8825/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Rental Real Estate
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 8825
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Form 8825 is used by partnerships and S
                      corporations to report rental real estate income and
                      expenses.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 8825
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* IRS Form 4562 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-4562/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Depreciation & Amortization
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 4562
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how Form 4562 is used to report depreciation,
                      amortization, and certain property deductions.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 4562
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* IRS Form 4797 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-4797/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Sales of Business Property
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 4797
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Form 4797 is used to report gains or losses from
                      the sale or exchange of business property.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 4797
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* IRS Form 7205 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-7205/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Energy-Efficient Buildings
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      IRS Form 7205
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Form 7205 is used to calculate and claim the
                      Energy Efficient Commercial Buildings Deduction for
                      qualifying property.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 7205
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box component="section" sx={{ backgroundColor: '#f0f2f9' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Comparisons & Insights
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Explore helpful business tax comparisons and insights to better
                understand filing requirements, tax treatment, reporting
                differences, and important business tax considerations.
              </Typography>
            </Grid>
            {/* Form 1120-S vs. Form 1120 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120s-vs-form-1120-comparison/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form Comparison
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S vs. Form 1120
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the key differences between Form 1120-S and
                      Form 1120, including tax treatment, filing requirements,
                      and how business income is reported.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Compare Form 1120-S vs. Form 1120
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* Form 1120-S vs. Form 1065 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120s-vs-form-1065-comparison/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form Comparison
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S vs. Form 1065
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Compare S corporation and partnership tax filing
                      requirements, including ownership, tax treatment,
                      schedules, and income reporting.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Compare Form 1120-S vs. Form 1065
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* Schedule K vs. Schedule K-1 */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/schedule-k-vs-schedule-k-1/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Schedule Comparison
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Schedule K vs. Schedule K-1
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn the difference between Schedule K and Schedule K-1
                      and how each reports business income, deductions, credits,
                      and other tax items.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Compare Schedule K vs. Schedule K-1
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* Separately Stated vs. Nonseparately Stated Items */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-separately-vs-nonseparately-stated-items/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Stated Items
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Separately Stated vs. Nonseparately Stated Items
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the difference between separately stated and
                      nonseparately stated items and how they are reported on an
                      S corporation tax return.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Stated Items
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* Form 1120-S Separately Stated Income Items */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-separately-stated-income-items-of-an-s-corporation/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Separately Stated
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S Separately Stated Income Items
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn which income, deductions, credits, and other tax
                      items must be separately stated and passed through to S
                      corporation shareholders.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Separately Stated Items
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            {/* Form 1120-S Stock and Debt Basis */}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/business-tax/form-1120-s-stock-and-debt-basis/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#f1e9ff',
                      color: '#43247b',
                      fontWeight: 500,
                      border: '1px solid #43247b',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Basis Reporting
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 1120-S Stock and Debt Basis
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how shareholders determine and track stock and
                      debt basis and how these amounts affect deductions for S
                      corporation losses.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Stock & Debt Basis
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}
        component="section"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need Help Filing Business Taxes?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Have the information you need? File your business tax return
              online with a guided e-filing experience
              <br /> designed to make filing simple and accurate.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=businesstax`}
              >
                Get Started Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default BusinessTax;
